import { useCallback, useEffect, useMemo, useState } from 'react';
import { useListVals, useObjectVal } from 'react-firebase-hooks/database';
import { firebaseApp } from '../firebase';
import { DB } from '../DB';
import {
    child,
    DataSnapshot,
    getDatabase,
    off,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
    push,
    ref,
    remove,
    serverTimestamp,
    set,
} from 'firebase/database';
import { Product, ProductPricing } from '../model/Product';
import { Category } from '../model/Category';

export interface PreOrder {
    preOrderId: string;
    productId: string;
    pricingId: string;
    preOrderTime: string; // ISO date string (e.g., '2025-06-01T12:00:00Z')
    createdDate: any;
    updatedDate: any;
}

// Interface for the simplified PreOrder from earlier
export interface PreOrder {
  preOrderId: string;
  productId: string;
  pricingId: string;
  preOrderTime: string; // ISO date string (e.g., '2025-06-01T12:00:00Z')
  createdDate: any;
  updatedDate: any;
}

// Interface for the new PreOrder structure from the provided data
export interface CustomerPreOrder {
  preOrderId: string;
  address: string;
  comments: string;
  firstName: string;
  lastName: string;
  phone: string;
  preOrderDate: string; // ISO date string
  totalQuantity: number;
  userId: string;
  items: PreOrderItem[];
}

export interface PreOrderItem {
  preOrderId: string;
  pricingId: string;
  productId: string;
  quantity: number;
}

const database = getDatabase(firebaseApp);

export const useCategories = () => {
    const [categories] = useListVals<Category>(ref(database, DB.categories));
    return { categories: categories ?? [] };
};

export const useDisplayCategories = () => {
    const [displayCategories, setDisplayCategories] = useState<Category[]>();
    const { categories } = useCategories();

    useEffect(() => {
        if (categories) {
            const filteredCategories = categories;
            filteredCategories.sort((a, b) => a.displayOrder - b.displayOrder);
            setDisplayCategories(filteredCategories);
        }
    }, [categories]);
    return { displayCategories };
};

export const useProducts = () => {
    const [snapshotsProduct, loading] = useListVals<Product>(ref(database, DB.products));
    const [products, setProducts] = useState<Product[]>([]);

    useEffect(() => {
        if (snapshotsProduct) {
            setProducts(snapshotsProduct);
        }
    }, [snapshotsProduct]);

    return { products, loading };
};

export const useProductsByCategory = () => {
    const { products } = useProducts();
    const [productsByCategory, setProductsByCategory] = useState<any>([]);

    useEffect(() => {
        if (products) {
            const groupByCategory = products.reduce((group: any, product) => {
                const { category } = product;
                group[category] = group[category] ?? [];
                group[category].push(product);
                return group;
            }, {});

            for (const category in groupByCategory) {
                groupByCategory[category].sort((a: Product, b: Product) =>
                    a.productNameEn.localeCompare(b.productNameEn),
                );
            }

            setProductsByCategory(groupByCategory);
        }
    }, [products]);

    return { productsByCategory };
};

export const useProductPricing = (productId?: string) => {
    const productPricingSnapshot = useMemo(
        () => ref(database, DB.productPricing + '/' + productId),
        [productId],
    );
    const [productPricingSnapshots, setProductPricingSnapshots] = useState<DataSnapshot[]>([]);

    useEffect(() => {
        let _onChildAdded: any = undefined;
        let _onChildChanged: any = undefined;
        let _onChildRemoved: any = undefined;

        const productPricingSnapshotsMap = new Map<string | null, DataSnapshot>();

        setTimeout(() => {
            _onChildAdded = onChildAdded(productPricingSnapshot, (snapshot) => {
                productPricingSnapshotsMap.set(snapshot.key, snapshot);
                const productPricingSnapshots = [...productPricingSnapshotsMap.values()].sort(
                    (p1, p2) => p1.val().displayOrder - p2.val().displayOrder,
                );
                setProductPricingSnapshots(productPricingSnapshots);
            });
            _onChildChanged = onChildChanged(productPricingSnapshot, (snapshot) => {
                productPricingSnapshotsMap.set(snapshot.key, snapshot);
                const productPricingSnapshots = [...productPricingSnapshotsMap.values()].sort(
                    (p1, p2) => p1.val().displayOrder - p2.val().displayOrder,
                );
                setProductPricingSnapshots(productPricingSnapshots);
            });
            _onChildRemoved = onChildRemoved(productPricingSnapshot, (snapshot) => {
                productPricingSnapshotsMap.delete(snapshot.key);
                const productPricingSnapshots = [...productPricingSnapshotsMap.values()].sort(
                    (p1, p2) => p1.val().displayOrder - p2.val().displayOrder,
                );
                setProductPricingSnapshots(productPricingSnapshots);
            });
        }, 500);

        return () => {
            off(productPricingSnapshot, 'child_added', _onChildAdded);
            off(productPricingSnapshot, 'child_changed', _onChildChanged);
            off(productPricingSnapshot, 'child_removed', _onChildRemoved);
        };
    }, [productId]);

    const addPricing = (productPricing: ProductPricing) => {
        productPricing.displayOrder = productPricing.displayOrder ?? 0;
        productPricing.createdDate = productPricing.createdDate ?? serverTimestamp();
        productPricing.updatedDate = serverTimestamp();

        const newKey = push(child(ref(database), DB.productPricing + '/' + productId)).key;
        productPricing.pricingId = newKey ?? new Date().getTime().toString();
        set(
            ref(database, DB.productPricing + '/' + productId + '/' + productPricing.pricingId),
            productPricing,
        );
    };

    const removePricing = useCallback((productPricingSnapshot: DataSnapshot) => {
        remove(productPricingSnapshot.ref);
    }, []);

    const updatePricing = useCallback(
        (productPricingSnapshot: DataSnapshot, productPricing: ProductPricing) => {
            productPricing.createdDate = productPricing.createdDate ?? serverTimestamp();
            productPricing.updatedDate = serverTimestamp();
            set(productPricingSnapshot.ref, productPricing);
        },
        [],
    );

    return { productPricingSnapshots, addPricing, removePricing, updatePricing };
};

export const useBatchProductPricing = (productIds: string[]) => {
    const [pricingSnapshotsMap, setPricingSnapshotsMap] = useState<Map<string, DataSnapshot[]>>(new Map());

    useEffect(() => {
        const productPricingSnapshotsMap = new Map<string, DataSnapshot[]>();
        const listeners: { productId: string; type: string; listener: any }[] = [];

        productIds.forEach((productId) => {
            if (!productId) return;
            const productPricingRef = ref(database, `${DB.productPricing}/${productId}`);
            const snapshotsMap = new Map<string | null, DataSnapshot>();

            const onChildAddedListener = onChildAdded(productPricingRef, (snapshot) => {
                snapshotsMap.set(snapshot.key, snapshot);
                productPricingSnapshotsMap.set(productId, [...snapshotsMap.values()].sort(
                    (p1, p2) => p1.val().displayOrder - p2.val().displayOrder,
                ));
                setPricingSnapshotsMap(new Map(productPricingSnapshotsMap));
            });

            const onChildChangedListener = onChildChanged(productPricingRef, (snapshot) => {
                snapshotsMap.set(snapshot.key, snapshot);
                productPricingSnapshotsMap.set(productId, [...snapshotsMap.values()].sort(
                    (p1, p2) => p1.val().displayOrder - p2.val().displayOrder,
                ));
                setPricingSnapshotsMap(new Map(productPricingSnapshotsMap));
            });

            const onChildRemovedListener = onChildRemoved(productPricingRef, (snapshot) => {
                snapshotsMap.delete(snapshot.key);
                productPricingSnapshotsMap.set(productId, [...snapshotsMap.values()].sort(
                    (p1, p2) => p1.val().displayOrder - p2.val().displayOrder,
                ));
                setPricingSnapshotsMap(new Map(productPricingSnapshotsMap));
            });

            listeners.push(
                { productId, type: 'child_added', listener: onChildAddedListener },
                { productId, type: 'child_changed', listener: onChildChangedListener },
                { productId, type: 'child_removed', listener: onChildRemovedListener },
            );
        });

        return () => {
            listeners.forEach(({ productId, type, listener }) => {
                off(ref(database, `${DB.productPricing}/${productId}`), type as any, listener);
            });
        };
    }, [productIds]);

    return { pricingSnapshotsMap };
};


export const usePreOrdersItems = () => {
  const [preOrdersSnapshots, loading] = useListVals<PreOrder>(ref(database, DB.preOrderItems));
  const [preOrdersItem, setPreOrdersItem] = useState<PreOrder[]>([]);

  useEffect(() => {
    if (preOrdersSnapshots) {
      setPreOrdersItem(preOrdersSnapshots);
    }
  }, [preOrdersSnapshots]);

  const addPreOrderItem = (preOrder: Omit<PreOrder, 'preOrderId' | 'createdDate' | 'updatedDate'>) => {
    const newKey = push(ref(database, DB.preOrderItems)).key;
    const preOrderData: PreOrder = {
      ...preOrder,
      preOrderId: newKey ?? new Date().getTime().toString(),
      createdDate: serverTimestamp(),
      updatedDate: serverTimestamp(),
    };
    set(ref(database, `${DB.preOrderItems}/${preOrderData.preOrderId}`), preOrderData);
  };

  const updatePreOrder = (preOrderId: string, preOrder: Partial<PreOrder>) => {
    const updatedData = {
      ...preOrder,
      updatedDate: serverTimestamp(),
    };
    set(ref(database, `${DB.preOrderItems}/${preOrderId}`), updatedData);
  };

  const removePreOrderItem = (preOrderId: string) => {
    remove(ref(database, `${DB.preOrderItems}/${preOrderId}`));
  };

  return { preOrdersItem, addPreOrderItem, updatePreOrder, removePreOrderItem, loading };
};

export const useCustomerPreOrders = () => {
  const [preOrdersSnapshots, loading] = useObjectVal<{ [key: string]: CustomerPreOrder }>(ref(database, DB.preOrders));
  const [customerPreOrders, setCustomerPreOrders] = useState<CustomerPreOrder[]>([]);

  useEffect(() => {
    if (preOrdersSnapshots) {
      const preOrdersArray = Object.entries(preOrdersSnapshots).map(([key, value]) => ({
        ...value as CustomerPreOrder,
        preOrderId: key,
      }));
      setCustomerPreOrders(preOrdersArray);
    }
  }, [preOrdersSnapshots]);

  return { customerPreOrders, loading };
};

